import React, { useState, useEffect } from "react"

import { Search, ContentCopy } from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
} from '@mui/material'

import { packageMappingsService, handleApiError, type PackageMappingsResponse } from "../api"

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`package-tabpanel-${index}`}
      aria-labelledby={`package-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function PackageMappingsPage() {
  const [tabValue, setTabValue] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [packageMappings, setPackageMappings] = useState<PackageMappingsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchPackageMappings = async () => {
      try {
        setIsLoading(true)
        setError("")
        const data = await packageMappingsService.getPackageMappings()
        setPackageMappings(data)
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setIsLoading(false)
      }
    }

    fetchPackageMappings()
  }, [])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setSearchTerm("") // Reset search when changing tabs
  }

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(text)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      setError('Failed to copy to clipboard')
    }
  }

  const filterPackages = (packages?: Record<string, { name: string; junctionName?: string; labTestId: string; labProvider: string; isContentOnly?: boolean }> ) => {
    if (!packages) return []
    if (!searchTerm) return Object.entries(packages)
    const term = searchTerm.toLowerCase()
    return Object.entries(packages).filter(
      ([key, pkg]) =>
        key.toLowerCase().includes(term) ||
        pkg.name.toLowerCase().includes(term) ||
        pkg.junctionName?.toLowerCase().includes(term) ||
        pkg.labTestId.toLowerCase().includes(term) ||
        pkg.labProvider.toLowerCase().includes(term)
    )
  }

  const filterQuestMappings = (mappings?: Record<string, string>) => {
    if (!mappings) return []
    if (!searchTerm) return Object.entries(mappings)
    const term = searchTerm.toLowerCase()
    return Object.entries(mappings).filter(
      ([key, value]) =>
        key.toLowerCase().includes(term) ||
        value.toLowerCase().includes(term)
    )
  }

  const getLabProviderColor = (provider: string) => {
    switch (provider.toUpperCase()) {
      case 'QUEST':
        return 'primary'
      case 'BIOREFERENCE':
        return 'secondary'
      case 'CONTENT':
        return 'info'
      default:
        return 'default'
    }
  }

  const renderPackageTable = (packages?: Record<string, { name: string; junctionName?: string; labTestId: string; labProvider: string; isContentOnly?: boolean }>, title?: string) => {
      const filteredPackages = filterPackages(packages)
      
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {title} ({filteredPackages.length} mappings)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Package ID / Key</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Junction Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Lab Test ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Lab Provider</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPackages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No packages found matching your search' : 'No packages configured'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPackages.map(([key, pkg]) => (
                    <TableRow key={key} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.813rem',
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={key}
                          >
                            {key}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={`Copy ${key}`}
                            onClick={() => handleCopy(key)}
                            sx={{ p: 0.25 }}
                          >
                            <ContentCopy
                              sx={{
                                fontSize: 16,
                                color: copiedId === key ? 'success.main' : 'action.active',
                              }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{pkg.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontStyle: pkg.junctionName ? 'normal' : 'italic' }}>
                          {pkg.junctionName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.813rem',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={pkg.labTestId}
                          >
                            {pkg.labTestId}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={`Copy ${pkg.labTestId}`}
                            onClick={() => handleCopy(pkg.labTestId)}
                            sx={{ p: 0.25 }}
                          >
                            <ContentCopy
                              sx={{
                                fontSize: 16,
                                color: copiedId === pkg.labTestId ? 'success.main' : 'action.active',
                              }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pkg.labProvider}
                          color={getLabProviderColor(pkg.labProvider)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {pkg.isContentOnly && (
                          <Chip
                            label="Content Only"
                            color="info"
                            size="small"
                            variant="filled"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )
    }

  const renderQuestToBioReferenceTable = (mappings?: Record<string, string>) => {
    const filteredMappings = filterQuestMappings(mappings)
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Quest to BioReference Mappings ({filteredMappings.length} mappings)
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          These mappings are used to automatically swap Quest packages to BioReference when customers are in NJ, RI, or NY states.
        </Alert>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Quest Package ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>→</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>BioReference Package ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No mappings found matching your search' : 'No mappings configured'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMappings.map(([questKey, bioRefKey]) => (
                  <TableRow key={questKey} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.813rem',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={questKey}
                        >
                          {questKey}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Copy ${questKey}`}
                          onClick={() => handleCopy(questKey)}
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopy
                            sx={{
                              fontSize: 16,
                              color: copiedId === questKey ? 'success.main' : 'action.active',
                            }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">→</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.813rem',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={bioRefKey}
                        >
                          {bioRefKey}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Copy ${bioRefKey}`}
                          onClick={() => handleCopy(bioRefKey)}
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopy
                            sx={{
                              fontSize: 16,
                              color: copiedId === bioRefKey ? 'success.main' : 'action.active',
                            }}
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={500} height={24} />
        </Box>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Package Mappings
          </Typography>
          {packageMappings && (
            <Chip
              label={packageMappings.environment === 'production' ? 'Production' : 'Development'}
              color={packageMappings.environment === 'production' ? 'success' : 'warning'}
              size="small"
            />
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          View all package configurations, Stripe product IDs, and lab provider mappings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by package ID, name, junction name, lab test ID, or provider..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Development/Sandbox" sx={{ fontWeight: 700, fontSize: '0.95rem', textTransform: 'none' }} />
              <Tab label="Production" sx={{ fontWeight: 700, fontSize: '0.95rem', textTransform: 'none' }} />
              <Tab label="Quest → BioReference Mappings" sx={{ fontWeight: 700, fontSize: '0.95rem', textTransform: 'none' }} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {renderPackageTable(packageMappings?.dev, "Development/Sandbox Packages")}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderPackageTable(packageMappings?.prod, "Production Packages")}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderQuestToBioReferenceTable(packageMappings?.questToBioReference)}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  )
}

